import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { ConfirmDialogConfig } from '../../stores/dialogStore';
import { LoadingButton } from '../shared/ui/Loading';

interface ConfirmDialogProps {
  config: ConfirmDialogConfig;
  onClose: () => void;
}

function ConfirmDialog({ config, onClose }: ConfirmDialogProps) {
  const [confirmText, setConfirmText] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  const handleConfirm = async () => {
    if (
      config.requireConfirmText &&
      confirmText !== config.requireConfirmText
    ) {
      return;
    }

    setIsProcessing(true);
    try {
      await config.onConfirm();
      onClose();
    } catch (_error) {
      // Error in confirm action - handled silently
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancel = (_event?: object, reason?: string) => {
    // disableEscapeKeyDown removed from MUI Dialog in v9; block escape here.
    if (config.disableEscapeKeyDown && reason === 'escapeKeyDown') {
      return;
    }
    if (config.onCancel) {
      config.onCancel();
    }
    onClose();
  };

  const isConfirmDisabled = Boolean(
    isProcessing ||
    (config.requireConfirmText && confirmText !== config.requireConfirmText),
  );

  return (
    <Dialog
      open={config.open}
      onClose={handleCancel}
      maxWidth={config.maxWidth || 'sm'}
      fullWidth={config.fullWidth !== false}
      aria-labelledby="confirm-dialog-title"
    >
      <DialogTitle id="confirm-dialog-title">
        <Typography variant="h4" className="mb-2">
          {config.title}
        </Typography>
        {config.subtitle && (
          <Typography variant="subtitle1" color="textSecondary">
            {config.subtitle}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent>
        {config.message && (
          <Typography variant="body1" sx={{ mb: 2 }}>
            {config.message}
          </Typography>
        )}

        {config.content}

        {config.requireConfirmText && (
          <div className="mt-3">
            <Typography variant="body2" sx={{ mb: 1 }}>
              Please type &quot;{config.requireConfirmText}&quot; to confirm:
            </Typography>
            <TextField
              fullWidth
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={config.requireConfirmText}
              disabled={isProcessing}
            />
          </div>
        )}

        {config.customActions && typeof config.customActions === 'function'
          ? config.customActions(confirmText)
          : config.customActions}
      </DialogContent>

      <DialogActions>
        {config.extraActions}

        <Button
          onClick={handleCancel}
          color="primary"
          variant="outlined"
          disabled={isProcessing}
        >
          {config.cancelText || 'Cancel'}
        </Button>

        <LoadingButton
          onClick={handleConfirm}
          color={config.confirmColor || 'primary'}
          variant={config.destructive ? 'contained' : 'contained'}
          disabled={isConfirmDisabled}
          loading={isProcessing}
          showExternalSpinner
        >
          {config.confirmText || 'Confirm'}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

export default ConfirmDialog;
